import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Delete, 
  Edit, 
  Visibility, 
  VisibilityOff,
  Article,
  Person,
  ArrowBack,
  Refresh,
  Search
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/api';

const AdminStories = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, story: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalStories, setTotalStories] = useState(0);

  useEffect(() => {
    loadStories();
  }, [page, limit, statusFilter]);

  const loadStories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate offset for pagination
      const offset = (page - 1) * limit;
      
      const response = await adminService.getStories({ 
        limit, 
        offset,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      });
      console.log('Stories API Response:', response);
      
      // Handle different response structures
      let storiesData = [];
      let total = 0;
      
      if (response.data?.data?.stories) {
        storiesData = response.data.data.stories;
        total = response.data.data.total || storiesData.length;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        storiesData = response.data.data;
        total = storiesData.length;
      } else if (response.data && Array.isArray(response.data)) {
        storiesData = response.data;
        total = storiesData.length;
      } else if (Array.isArray(response)) {
        storiesData = response;
        total = storiesData.length;
      }
      
      console.log('Processed stories data:', storiesData);
      if (storiesData.length > 0) {
        console.log('First story structure:', storiesData[0]);
        console.log('Available fields:', Object.keys(storiesData[0]));
      }
      
      setStories(Array.isArray(storiesData) ? storiesData : []);
      setTotalStories(total);
    } catch (err) {
      console.error('Error loading stories:', err);
      setError('Failed to load stories');
      setStories([]); // Ensure it's always an array
      setTotalStories(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStory = async (storyId) => {
    try {
      await adminService.deleteStory(storyId);
      setStories(prevStories => 
        Array.isArray(prevStories) 
          ? prevStories.filter(story => story.story_id !== storyId)
          : []
      );
      setDeleteDialog({ open: false, story: null });
      // Reload to get accurate count
      loadStories();
    } catch (err) {
      console.error('Error deleting story:', err);
      setError('Failed to delete story');
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1); // Reset to first page when changing limit
  };

  const handleSearch = () => {
    setPage(1); // Reset to first page when searching
    loadStories();
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const filteredStories = Array.isArray(stories) ? stories.filter(story => {
    // Status filter
    if (statusFilter && story.status !== statusFilter) {
      return false;
    }
    
    if (!searchTerm) return true; // If no search term, show all stories from current page
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in title
    if (story.title?.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search in various author field possibilities
    if (story.author_name?.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    if (story.author && typeof story.author === 'string' && story.author.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    if (story.author && typeof story.author === 'object') {
      const firstName = story.author.given_name || story.author.first_name || '';
      const lastName = story.author.family_name || story.author.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const authorName = story.author.name || story.author.username || '';
      
      if (fullName.toLowerCase().includes(searchLower) || 
          authorName.toLowerCase().includes(searchLower)) {
        return true;
      }
    }
    
    if (story.user_name?.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    if (story.created_by?.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    return false;
  }) : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      default: return 'default';
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'public': return 'success';
      case 'private': return 'error';
      case 'invited': return 'info';
      default: return 'default';
    }
  };

  const totalPages = Math.ceil(totalStories / limit);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Article sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Manage Stories
          </Typography>
        </Box>
        <IconButton onClick={loadStories}>
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Page Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Chip 
          label={`Published: ${Array.isArray(stories) ? stories.filter(s => s.status === 'published').length : 0}`}
          color="success"
          variant="outlined"
        />
        <Chip 
          label={`Drafts: ${Array.isArray(stories) ? stories.filter(s => s.status === 'draft').length : 0}`}
          color="warning"
          variant="outlined"
        />
        <Typography variant="body2" color="text.secondary">
          Page {page} of {totalPages} • {stories.length} stories shown
        </Typography>
      </Box>

      {/* Search and Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search stories by title or author..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Stories per page</InputLabel>
              <Select
                value={limit}
                label="Stories per page"
                onChange={handleLimitChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stories Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Story</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Visibility</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? 'No stories found matching your search' : 'No stories found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStories.map((story) => (
                <TableRow key={story.story_id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {story.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {story.story_id?.substring(0, 8)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                        <Person />
                      </Avatar>
                      <Typography variant="body2">
                        {(() => {
                          // Try different possible author field structures
                          if (story.author_name) {
                            return story.author_name;
                          }
                          if (story.author && typeof story.author === 'string') {
                            return story.author;
                          }
                          if (story.author && typeof story.author === 'object') {
                            const firstName = story.author.given_name || story.author.first_name || '';
                            const lastName = story.author.family_name || story.author.last_name || '';
                            const fullName = `${firstName} ${lastName}`.trim();
                            if (fullName) return fullName;
                            return story.author.name || story.author.username || 'Unknown Author';
                          }
                          if (story.user_name) {
                            return story.user_name;
                          }
                          if (story.created_by) {
                            return story.created_by;
                          }
                          return 'Unknown Author';
                        })()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={story.status || 'Unknown'}
                      color={getStatusColor(story.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={story.visibility || 'Unknown'}
                      color={getVisibilityColor(story.visibility)}
                      icon={
                        story.visibility === 'public' ? <Visibility /> :
                        story.visibility === 'private' ? <VisibilityOff /> :
                        <Person />
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {story.view_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {story.created_at ? new Date(story.created_at).toLocaleDateString() : 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Story">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => window.open(`/story/${story.story_id}`, '_blank')}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Story">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, story })}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalStories)} of {totalStories} stories
        </Typography>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, story: null })}
      >
        <DialogTitle>Delete Story</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.story?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, story: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteStory(deleteDialog.story?.story_id)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminStories;
